import { useEffect, useMemo, useState } from "react";
import { Accordion } from "react-migration/components/Accordion";
import { PlanningApplication } from "react-migration/domains/planning/types";
import { applicationsByAddress } from "../SelectionSidebar/applicationsByAddress";
import { Nullable } from "src/js/types/Nullable";
import { PlanningApplicationList } from "react-migration/domains/planning/components/PlanningApplicationList";
import cn from "clsx";
import { ExpandibleList } from "react-migration/components/ExpandibleList";
import { PlanningApplicationClassification } from "react-migration/domains/planning/components/PlanningApplicationClassification";

function usePlanningApplicationsByAddress<T extends PlanningApplication>(planningApplications: Nullable<T[]>) {
  return useMemo(() => {
    if (planningApplications?.length)
      return applicationsByAddress(planningApplications as unknown as PlanningApplication[]);
  }, [planningApplications]);
}

interface PlanningApplicationsByAddressProps<T extends PlanningApplication = PlanningApplication> {
  planningApplications: T[];
  onApplicationClick?: (application: T) => void;
}

export function PlanningApplicationsByAddress({
  planningApplications,
  onApplicationClick,
}: PlanningApplicationsByAddressProps) {
  const applicationsByAddress = usePlanningApplicationsByAddress(planningApplications);

  const [expandedAccordionItems, setExpandedAccordionItems] = useState<string[]>([]);

  useEffect(() => {
    if (applicationsByAddress?.length === 1) {
      setExpandedAccordionItems([applicationsByAddress[0].address]);
    } else {
      setExpandedAccordionItems([]);
    }
  }, [applicationsByAddress]);

  if (!applicationsByAddress) return null;

  return (
    <Accordion value={expandedAccordionItems} onValueChange={setExpandedAccordionItems} type="multiple">
      <ExpandibleList limit={8} itemsName="addresses">
        {applicationsByAddress.map(({ address, applications, classifications }) => (
          <Accordion.Item key={address} value={address} className="group">
            <Accordion.Trigger asChild>
              <div
                className="flex gap-1 items-center px-2 py-0.5"
                data-testid="planning-selection-sidebar-accordion-trigger"
              >
                <div className="grow text-left truncate text-ellipsis text-xs font-medium">{address}</div>
                <div className="shrink-0 flex gap-1 items-center">
                  <>
                    {[...classifications]
                      .sort((a, b) => a.localeCompare(b))
                      .map((classification) => (
                        <PlanningApplicationClassification key={classification} classification={classification} />
                      ))}
                    <i
                      className={cn("text-2xl", {
                        "icon-lt-arrow-up-s-line": expandedAccordionItems.includes(address),
                        "icon-lt-arrow-down-s-line": !expandedAccordionItems.includes(address),
                      })}
                    />
                  </>
                </div>
              </div>
            </Accordion.Trigger>
            <Accordion.Content className="border-t border-neutral-200">
              <PlanningApplicationList onApplicationClick={onApplicationClick} planningApps={applications} />
            </Accordion.Content>
          </Accordion.Item>
        ))}
      </ExpandibleList>
    </Accordion>
  );
}
